import { useState } from "react";
import { Card, Typography, Upload, App, Image, Progress, Alert, Flex } from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import CustomButton from "../../components/CustomButton/Button";
import { handleUploadFile } from "../../helpers/helpers";
import Logo from "../../assets/images/auth-logo.png";
import SampleBrDocUrl from "../../assets/docs/board_resolution.docx";
import { useSelector, useDispatch } from "react-redux";
import { saveBoardResolution } from "../../redux/slices/DocumentSlice";
import { useNavigate } from "react-router-dom";
import "./BoardResolution.scss";

const { Title, Text } = Typography;

const BoardResolution = () => {
    const { message } = App.useApp();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const { loading, error } = useSelector((state) => state.docs);

    // SINGLE STATE OBJECT
    const [state, setState] = useState({
        fileUrl: null,
        fileName: null,
        uploading: false,
        progress: 0,
    });

    const updateState = (patch) => setState((prev) => ({ ...prev, ...patch }));

    const { fileUrl, fileName, uploading, progress } = state;

    // VALIDATION
    const beforeUpload = (file) => {
        if (!["application/pdf"].includes(file.type)) {
            message.error("Only PDF files allowed.");
            return Upload.LIST_IGNORE;
        }
        if (file.size / 1024 / 1024 > 5) {
            message.error("File must be under 5MB.");
            return Upload.LIST_IGNORE;
        }
        return true;
    };

    // UPLOAD FILE
    const handleUpload = async (file, onSuccess, onError) => {
        try {
            updateState({ uploading: true, progress: 0 });

            const orgId = user?.orgId;

            // 1️⃣ Clean spaces and special chars — replace with underscore
            const cleanName = file.name.replace(/\s+/g, "_");

            // 2️⃣ Append orgId before extension
            const ext = cleanName.substring(cleanName.lastIndexOf("."));
            const base = cleanName.substring(0, cleanName.lastIndexOf("."));
            const finalFileName = `${base}_${orgId}${ext}`;

            // 3️⃣ Re-create file with new name
            const renamedFile = new File([file], finalFileName, {
                type: file.type,
            });

            const path = `board-resolution/${orgId}`;

            const response = await handleUploadFile(renamedFile, path, (p) =>
                updateState({ progress: p })
            );

            updateState({
                fileUrl: response.fileUrl,
                fileName: response.fileName,
            });

            onSuccess("ok");
        } catch (err) {
            message.error("Upload failed.");
            onError(err);
        } finally {
            updateState({ uploading: false });
        }
    };

    // SUBMIT
    const handleSubmit = async () => {
        if (!fileUrl) {
            message.error("Please upload a document first.");
            return;
        }

        try {
            const payload = { url: fileUrl };
            const result = await dispatch(saveBoardResolution(payload));

            if (result?.payload?.statusCode === 201) {
                message.success("Board Resolution submitted successfully!");
                navigate("/onboarding");
            } else {
                message.error("Failed to save Board Resolution.");
            }
        } catch {
            message.error("Something went wrong.");
        }
    };

    return (
        <div className="br-container">
            <div className="logo">
                <Image preview={false} src={Logo} alt="Logo" />
            </div>

            <Card className="br-card">
                <Title level={4} className="br-title">Board Resolution</Title>

                {/* STEP 1 */}
                <div className="step-container">
                    <Title level={5} className="step-title">STEP 1 - Download Sample</Title>

                    <Text className="step-text">
                        Download the sample Board Resolution document, fill it with the
                        company details, obtain board signatures.
                    </Text>

                    <CustomButton
                        variant="outlined"
                        color="primary"
                        icon={<DownloadOutlined />}
                        onClick={() => window.open(SampleBrDocUrl, "_blank")}
                    >
                        Download Sample Document
                    </CustomButton>
                </div>

                {/* STEP 2 */}
                <div className="step-container">
                    <Title level={5} className="step-title">STEP 2 - Upload Signed PDF</Title>

                    <Text className="step-text">
                        Upload the finalized and signed Board Resolution in PDF format.
                    </Text>

                    <Flex gap="middle" align="center">
                        <Upload
                            maxCount={1}
                            accept=".pdf"
                            beforeUpload={beforeUpload}
                            customRequest={({ file, onSuccess, onError }) =>
                                handleUpload(file, onSuccess, onError)
                            }
                            showUploadList={false}
                        >
                            <CustomButton type="primary" icon={<UploadOutlined />}>
                                Upload PDF
                            </CustomButton>
                        </Upload>

                        {fileName && <Text className="file-name">{fileName}</Text>}
                    </Flex>


                    {uploading && <Progress percent={progress} size="small" />}
                </div>

                {error && <Alert message={error} type="error" showIcon />}

                {/* SUBMIT */}
                <CustomButton
                    type="primary"
                    block
                    loading={loading}
                    style={{ marginTop: 20 }}
                    onClick={handleSubmit}
                >
                    Submit Board Resolution
                </CustomButton>
                <CustomButton
                    style={{ marginTop: 16 }}
                    block
                    variant="link"
                    onClick={() => navigate("/onboarding")}
                >
                    Maybe Later
                </CustomButton>
            </Card>
        </div>
    );
};

export default BoardResolution;
